import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { MdPerson } from "react-icons/md";
import { apiUrl } from "utils/apiUrl";
import { clientSafeMessage } from "utils/clientSafeMessage";
import {
  normalizeCredentialsHint,
  toCredentialMessage,
} from "utils/credentialClientMessage";
import { computeExpiryIsoDate, getPlanById } from "constants/clientSlotPlans";
import ConnectTradingAccountInstructions from "components/client/ConnectTradingAccountInstructions";
import SlaveAccountRowStatus from "components/client/SlaveAccountRowStatus";
import SlaveAccountRowToolbar from "components/client/SlaveAccountRowToolbar";
import { useClientDuplikiumSlaveRows } from "hooks/useClientDuplikiumSlaveRows";
import { readClientSession } from "utils/clientSession";
import { buildSlotsOwnedUrl } from "utils/subscriptionPath";
import { buildSlotExtensionPayUrl } from "utils/slotExtensionPay";
import { PERSONAL_BROKER_PROMO_CODE } from "constants/clientSlotPlans";
import SubscriptionBuySlotPanel from "components/client/SubscriptionBuySlotPanel";

const DEFAULT_PLAN_ID = "bronze";

/** Set to false to re-enable this page. */
const PATH2_TEMPORARILY_DISABLED = true;

export default function SubscriptionPath2PersonnalBroker() {
  const session = readClientSession();
  const {
    accounts,
    allAccounts,
    setAccounts,
    accountsLoading,
    accountsLoadError,
    loadAccounts,
    effectiveClientId,
  } = useClientDuplikiumSlaveRows(session, {
    subscriptionPath: "path2",
    pollMs: 60000,
  });
  const [accountType, setAccountType] = useState("MT5");
  const [accountNumber, setAccountNumber] = useState("");
  const [server, setServer] = useState("");
  const [tradingPassword, setTradingPassword] = useState("");
  const [selectedSlot, setSelectedSlot] = useState("");
  const [paidSlotsCount, setPaidSlotsCount] = useState(0);
  const [awaitingApprovalSlots, setAwaitingApprovalSlots] = useState([]);
  const [connectLoadingId, setConnectLoadingId] = useState(null);
  const [addingAccount, setAddingAccount] = useState(false);
  const [actionLoadingId, setActionLoadingId] = useState(null);
  const [connectError, setConnectError] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [statusLoadingId, setStatusLoadingId] = useState(null);
  const [editForm, setEditForm] = useState({
    accountType: "MT5",
    accountNumber: "",
    server: "",
    tradingPassword: "",
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(apiUrl(buildSlotsOwnedUrl("path2")), {
          credentials: "include",
        });
        if (!res.ok) return;
        const data = await res.json().catch(() => ({}));
        const n = Number(data?.paidSlotsCount);
        if (!cancelled && Number.isFinite(n) && n >= 0) {
          setPaidSlotsCount(n);
        }
        if (!cancelled && Array.isArray(data?.awaitingApprovalSlots)) {
          setAwaitingApprovalSlots(data.awaitingApprovalSlots);
        }
      } catch {
        /* API not available yet — paidSlotsCount stays at its current value. */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [effectiveClientId]);

  const usedSlotsGlobal = useMemo(
    () =>
      new Set(
        (allAccounts || accounts)
          .map((a) => Number(a.slot))
          .filter((s) => Number.isFinite(s) && s > 0),
      ),
    [allAccounts, accounts],
  );

  const availableSlots = useMemo(() => {
    const list = [];
    const need = Math.max(0, paidSlotsCount - accounts.length);
    let s = 1;
    while (list.length < need && s < 200) {
      if (!usedSlotsGlobal.has(s)) list.push(s);
      s += 1;
    }
    return list;
  }, [paidSlotsCount, accounts.length, usedSlotsGlobal]);

  useEffect(() => {
    if (availableSlots.length === 0) {
      if (selectedSlot !== "") setSelectedSlot("");
      return;
    }
    if (!availableSlots.includes(Number(selectedSlot))) {
      setSelectedSlot(String(availableSlots[0]));
    }
  }, [availableSlots, selectedSlot]);

  const canAddAccount = paidSlotsCount > 0 && availableSlots.length > 0;

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!canAddAccount) {
      setConnectError(
        paidSlotsCount === 0
          ? "You must buy a slot before adding a trading account."
          : "All your paid slots are already used.",
      );
      return;
    }
    const slotNum = Number(selectedSlot);
    if (!Number.isFinite(slotNum) || slotNum < 1) {
      setConnectError("Please select a slot.");
      return;
    }
    if (!accountNumber.trim() || !server.trim() || !tradingPassword.trim()) return;

    setAddingAccount(true);
    setConnectError("");
    try {
      const res = await fetch(apiUrl("/api/client/copy-save-slave-draft"), {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          clientId: effectiveClientId ?? session?.clientId ?? null,
          slot: slotNum,
          accountNumber: accountNumber.trim(),
          platform: accountType,
          server: server.trim(),
          tradingPassword: tradingPassword.trim(),
          subscriptionPath: "path2",
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) {
        setConnectError(
          clientSafeMessage(data.message, "Could not save your trading account."),
        );
        return;
      }
      setAccountNumber("");
      setServer("");
      setTradingPassword("");
      await loadAccounts();
      setConnectError("Account saved. Allow at least 1 min for system before connecting.");
    } catch (err) {
      setConnectError(clientSafeMessage(err?.message, "Could not save your trading account."));
    } finally {
      setAddingAccount(false);
    }
  };

  const handleConnectToSystem = async (accountId) => {
    if (editingId != null) {
      setConnectError("Please save before connecting.");
      return;
    }
    if (!accountId || connectLoadingId) return;
    const rowIndex = accounts.findIndex((a) => a.id === accountId);
    if (rowIndex < 0) return;
    const target = accounts[rowIndex];
    if (!target || target.linkedToCopier) return;
    const plan = getPlanById(DEFAULT_PLAN_ID);
    if (!plan) {
      setConnectError("Plan configuration missing.");
      return;
    }
    setConnectLoadingId(accountId);
    setConnectError("");
    const activatingDate = new Date().toISOString().slice(0, 10);
    const expiringDate = computeExpiryIsoDate(activatingDate, plan.months);

    try {
      const pwd = target.tradingPassword || "";
      const res = await fetch(apiUrl("/api/client/copy-provision-slave"), {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          clientId: effectiveClientId ?? session?.clientId ?? null,
          slot: Number(target.slot) || rowIndex + 1,
          accountNumber: target.accountNumber,
          platform: target.accountType || "MT5",
          server: target.server,
          tradingPassword: pwd,
          planId: plan.id,
          activatingDate,
          expiringDate,
          connectOnly: false,
          subscriptionPath: "path2",
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (data.skipped) {
        setConnectError(
          clientSafeMessage(
            data.message,
            "Please sign in with your account to connect your trading account.",
          ),
        );
        return;
      }
      if (!res.ok || !data.ok) {
        setConnectError(
          toCredentialMessage(
            data.message,
            "We could not connect your trading account. Check your credentials and try again.",
          ),
        );
        return;
      }
      if (data.copySettingsWarning) {
        console.warn(
          "[subscription] copy settings (Duplikium):",
          data.copySettingsWarning,
        );
      }
      await loadAccounts();
    } catch (err) {
      const raw = String(err?.message || "");
      setConnectError(
        /failed to fetch|networkerror|load failed/i.test(raw)
          ? "The server took too long (or the connection dropped). Wait 1–2 minutes, click Refresh — if still not Connected, use Edit to re-enter the password then Connect again."
          : clientSafeMessage(raw, "Connection failed."),
      );
    } finally {
      setConnectLoadingId(null);
    }
  };

  const startEditing = (row) => {
    setDeleteConfirmId(null);
    setEditingId(row.id);
    setEditForm({
      accountType: row.accountType || "MT5",
      accountNumber: row.accountNumber || "",
      server: row.server || "",
      tradingPassword: row.tradingPassword || "",
    });
    setConnectError("");
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditForm({
      accountType: "MT5",
      accountNumber: "",
      server: "",
      tradingPassword: "",
    });
  };

  const handleEditCredentials = async (accountId) => {
    if (!accountId || actionLoadingId || connectLoadingId) return;
    const target = accounts.find((a) => a.id === accountId);
    if (!target) return;

    const nextType = String(editForm.accountType || "").trim().toUpperCase();
    const accountNumberTrim = String(editForm.accountNumber || "").trim();
    const serverTrim = String(editForm.server || "").trim();
    const passwordTrim = String(editForm.tradingPassword || "").trim();

    if (nextType !== "MT4" && nextType !== "MT5") {
      setConnectError("Type must be MetaTrader 4 or MetaTrader 5.");
      return;
    }
    if (!accountNumberTrim || !serverTrim || !passwordTrim) {
      setConnectError("Type, Account Number, Server and Password are required.");
      return;
    }

    setActionLoadingId(accountId);
    setConnectError("");
    try {
      const res = await fetch(apiUrl("/api/client/copy-update-slave-credentials"), {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          clientId: effectiveClientId ?? session?.clientId ?? null,
          slot: Number(target.slot),
          accountNumber: accountNumberTrim,
          platform: nextType,
          server: serverTrim,
          tradingPassword: passwordTrim,
          subscriptionPath: "path2",
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.status === 404) {
        setConnectError(
          clientSafeMessage(
            data.message,
            "Could not update this account. Try Add again or Connect to system.",
          ),
        );
        return;
      }
      if (!res.ok || !data.ok) {
        setConnectError(
          toCredentialMessage(data.message, "Could not update your account."),
        );
        return;
      }

      setAccounts((prev) =>
        prev.map((acc) =>
          acc.id === accountId
            ? {
                ...acc,
                accountType: nextType,
                accountNumber: accountNumberTrim,
                server: serverTrim,
                tradingPassword: passwordTrim,
                tradingPasswordMasked: "•".repeat(Math.min(passwordTrim.length, 12)),
                tradeCopierAccountId:
                  data.tradeCopierAccountId ?? acc.tradeCopierAccountId ?? null,
                linkedToCopier: true,
                brokerConnected: data.brokerConnected === true,
                brokerState:
                  data.brokerState != null ? String(data.brokerState) : null,
                balanceDisplay: data.balanceDisplay ?? null,
                credentialsHint: data.credentialsHint ?? null,
              }
            : acc,
        ),
      );
      cancelEditing();
      if (data.copySettingsWarning) {
        console.warn(
          "[subscription] copy settings after save (Duplikium):",
          data.copySettingsWarning,
        );
      }
      setConnectError("Allow at least 1 min for system before connecting.");
    } catch (err) {
      setConnectError(
        clientSafeMessage(err?.message, "Credentials update failed."),
      );
    } finally {
      setActionLoadingId(null);
    }
  };

  const refreshBrokerStatus = async (accountId) => {
    const target = accounts.find((a) => a.id === accountId);
    if (!target?.slot) return;
    setStatusLoadingId(accountId);
    setConnectError("");
    try {
      const res = await fetch(apiUrl("/api/client/copy-slave-broker-status"), {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          clientId: effectiveClientId ?? session?.clientId ?? null,
          slot: Number(target.slot),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) {
        setConnectError(
          toCredentialMessage(data.message, "Status refresh failed."),
        );
        return;
      }
      setAccounts((prev) =>
        prev.map((acc) =>
          acc.id === accountId
            ? {
                ...acc,
                brokerConnected: data.brokerConnected === true,
                brokerState:
                  data.brokerState != null ? String(data.brokerState) : null,
                tradeCopierAccountId:
                  data.tradeCopierAccountId ?? acc.tradeCopierAccountId ?? null,
                linkedToCopier: true,
                balanceDisplay: data.balanceDisplay ?? null,
                credentialsHint: data.credentialsHint ?? null,
                activatingDate: data.activatingDate ?? acc.activatingDate ?? null,
                expiringDate: data.expiringDate ?? acc.expiringDate ?? null,
              }
            : acc,
        ),
      );
    } catch (e) {
      setConnectError(toCredentialMessage(e?.message, "Status refresh failed."));
    } finally {
      setStatusLoadingId(null);
    }
  };

  const handleDeleteAccount = async (accountId) => {
    if (!accountId || actionLoadingId || connectLoadingId) return;
    const target = accounts.find((a) => a.id === accountId);
    if (!target) return;

    setActionLoadingId(accountId);
    setConnectError("");
    try {
      if (target.slot != null) {
        const res = await fetch(apiUrl("/api/client/copy-delete-slave"), {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            clientId: effectiveClientId ?? session?.clientId ?? null,
            slot: Number(target.slot),
            tradeCopierAccountId: target.tradeCopierAccountId ?? null,
          }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || !data.ok) {
          setConnectError(
            clientSafeMessage(data.message, "Could not remove this account."),
          );
          return;
        }
      }
      setAccounts((prev) => prev.filter((acc) => acc.id !== accountId));
      if (deleteConfirmId === accountId) setDeleteConfirmId(null);
      await loadAccounts();
    } catch (err) {
      setConnectError(clientSafeMessage(err?.message, "Delete failed."));
    } finally {
      setActionLoadingId(null);
    }
  };

  if (PATH2_TEMPORARILY_DISABLED) {
    return (
      <div className="space-y-6">
        <div className="rounded-[20px] border border-gray-200 bg-white p-6 shadow-sm dark:border-white/10 dark:bg-navy-800">
          <div className="flex items-center gap-2">
            <MdPerson className="h-5 w-5 text-sky-600 dark:text-sky-300" />
            <h2 className="text-xl font-bold text-navy-700 dark:text-white">
              Path 2: Personal Broker
            </h2>
          </div>
          <p
            className="mt-2 block w-fit rounded-xl border border-amber-200 bg-amber-50 px-3 py-1.5 text-sm font-medium text-amber-900 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-100"
            role="status"
          >
            This page is temporarily disabled.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="rounded-[20px] border border-gray-200 bg-white p-6 shadow-sm dark:border-white/10 dark:bg-navy-800">
        <div className="flex items-center gap-2">
          <MdPerson className="h-5 w-5 text-sky-600 dark:text-sky-300" />
          <h2 className="text-xl font-bold text-navy-700 dark:text-white">
            Path 2: Personal Broker
          </h2>
        </div>
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
          Personal broker onboarding guide (no partner-broker sign-up required).
        </p>

        <ol className="mt-5 space-y-4 rounded-xl border border-gray-100 bg-gray-50 p-4 text-sm dark:border-white/10 dark:bg-navy-900/40">
          <SubscriptionBuySlotPanel
            subscriptionPath="path2"
            promoCode={PERSONAL_BROKER_PROMO_CODE}
            stepNumber={1}
          />
          <li>
            <span className="font-semibold text-navy-700 dark:text-white">
              2. Add your trading account.
            </span>
          </li>
        </ol>

        <ConnectTradingAccountInstructions />

        <div className="mt-4 flex flex-wrap items-center gap-2 rounded-xl border border-gray-100 bg-white px-4 py-3 text-sm shadow-sm dark:border-white/10 dark:bg-navy-900/50">
          <span className="font-semibold text-navy-700 dark:text-white">
            Slot: {paidSlotsCount > 0 ? (
              <span className="ml-1 inline-flex items-center gap-1">
                <span className="rounded-md bg-brand-50 px-2 py-0.5 font-bold text-brand-700 dark:bg-brand-500/15 dark:text-brand-200">
                  {accounts.length}/{paidSlotsCount}
                </span>
                <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                  used / paid
                </span>
              </span>
            ) : awaitingApprovalSlots.length > 0 ? (
              <span className="ml-1 rounded-md bg-amber-50 px-2 py-0.5 text-xs font-bold text-amber-800 dark:bg-amber-500/15 dark:text-amber-200">
                Waiting for Admin Approval
                {awaitingApprovalSlots[0]?.orderId
                  ? ` (${awaitingApprovalSlots[0].orderId})`
                  : ""}
              </span>
            ) : (
              <span className="ml-1 rounded-md bg-amber-50 px-2 py-0.5 text-xs font-bold text-amber-700 dark:bg-amber-500/15 dark:text-amber-200">
                None
              </span>
            )}
          </span>
        </div>

        {awaitingApprovalSlots.length > 0 && paidSlotsCount === 0 ? (
          <div className="mt-4 rounded-xl border border-amber-300/60 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-200">
            Payment received — your slot is <strong>Waiting for Admin Approval</strong>.
            You cannot connect a trading account until an administrator verifies the
            payment and approves it.
          </div>
        ) : null}

        {paidSlotsCount === 0 && awaitingApprovalSlots.length === 0 ? (
          <div className="mt-4 rounded-xl border border-amber-300/60 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-200">
            You must create a slot before adding a trading account. Click{" "}
            <strong>Create a slot</strong> above, then <strong>Pay</strong>.
          </div>
        ) : null}

        <form onSubmit={handleAdd} className="mt-6 space-y-4">
          <h3 className="text-base font-bold text-navy-700 dark:text-white">Add Account</h3>
          <fieldset
            disabled={!canAddAccount}
            className="grid grid-cols-1 gap-3 lg:grid-cols-6 disabled:opacity-60"
          >
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                Slot
              </label>
              <select
                value={selectedSlot}
                onChange={(e) => setSelectedSlot(e.target.value)}
                className="h-11 w-full rounded-xl border border-gray-200 bg-white px-3 text-sm text-navy-700 outline-none focus:border-brand-400 disabled:cursor-not-allowed dark:border-white/10 dark:bg-navy-900 dark:text-white"
              >
                {availableSlots.length === 0 ? (
                  <option value="">—</option>
                ) : (
                  availableSlots.map((n) => (
                    <option key={n} value={n}>
                      Slot {n}
                    </option>
                  ))
                )}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                Type
              </label>
              <select
                value={accountType}
                onChange={(e) => {
                  setAccountType(e.target.value);
                  setServer("");
                }}
                className="h-11 w-full rounded-xl border border-gray-200 bg-white px-3 text-sm text-navy-700 outline-none focus:border-brand-400 dark:border-white/10 dark:bg-navy-900 dark:text-white"
              >
                <option value="MT4">MetaTrader4</option>
                <option value="MT5">MetaTrader5</option>
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                Account Number
              </label>
              <input
                value={accountNumber}
                onChange={(e) => setAccountNumber(e.target.value)}
                className="h-11 w-full rounded-xl border border-gray-200 bg-white px-3 text-sm text-navy-700 outline-none focus:border-brand-400 dark:border-white/10 dark:bg-navy-900 dark:text-white"
                placeholder="Enter account number"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                Server
              </label>
              <input
                value={server}
                onChange={(e) => setServer(e.target.value)}
                className="h-11 w-full rounded-xl border border-gray-200 bg-white px-3 text-sm text-navy-700 outline-none focus:border-brand-400 dark:border-white/10 dark:bg-navy-900 dark:text-white"
                placeholder="Enter server name"
                autoComplete="off"
                spellCheck={false}
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                Trading Password
              </label>
              <input
                type="password"
                value={tradingPassword}
                onChange={(e) => setTradingPassword(e.target.value)}
                className="h-11 w-full rounded-xl border border-gray-200 bg-white px-3 text-sm text-navy-700 outline-none focus:border-brand-400 dark:border-white/10 dark:bg-navy-900 dark:text-white"
                placeholder="Enter trading password"
              />
            </div>
            <div className="flex items-end">
              <button
                type="submit"
                disabled={addingAccount || !canAddAccount}
                className="h-11 w-full rounded-xl bg-brand-500 px-4 text-sm font-semibold text-white transition hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {addingAccount ? "Saving…" : "Add"}
              </button>
            </div>
          </fieldset>
        </form>

        {accountsLoading && accounts.length === 0 ? (
          <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
            Loading your accounts…
          </p>
        ) : null}

        {accounts.length > 0 ? (
          <div className="mt-6">
            <h4 className="mb-3 text-sm font-bold text-navy-700 dark:text-white">
              Added accounts
            </h4>
            {connectError || accountsLoadError ? (
              <p className="mb-3 text-sm text-red-600 dark:text-red-400">
                {clientSafeMessage(
                  connectError || accountsLoadError,
                  "Something went wrong. Please try again.",
                )}
              </p>
            ) : null}
            <ul className="space-y-2">
              {accounts.map((row) => (
                <li
                  key={row.id}
                  className="flex flex-col gap-2 rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm dark:border-white/10 dark:bg-navy-900/50"
                >
                  <div className="flex flex-col gap-2">
                    <div className="flex flex-wrap items-center gap-2 overflow-x-auto py-0.5 [&_input]:box-border [&_input]:h-9 [&_select]:box-border [&_select]:h-9">
                    {editingId === row.id ? (
                      <>
                        <select
                          value={editForm.accountType}
                          onChange={(e) =>
                            setEditForm((p) => ({
                              ...p,
                              accountType: e.target.value,
                              server: "",
                            }))
                          }
                          className="h-9 shrink-0 rounded-lg border border-gray-200 bg-white px-2 text-xs leading-none text-navy-700 outline-none focus:border-brand-400 dark:border-white/10 dark:bg-navy-900 dark:text-white"
                        >
                          <option value="MT4">MetaTrader4</option>
                          <option value="MT5">MetaTrader5</option>
                        </select>
                        <span className="rounded-md bg-gray-200 px-2 py-0.5 text-xs font-semibold text-gray-700 dark:bg-white/10 dark:text-gray-300">
                          S{row.slot}
                        </span>
                        <input
                          value={editForm.accountNumber}
                          onChange={(e) =>
                            setEditForm((p) => ({ ...p, accountNumber: e.target.value }))
                          }
                          className="h-9 w-[115px] shrink-0 rounded-lg border border-gray-200 bg-white px-2 text-xs leading-none text-navy-700 outline-none focus:border-brand-400 dark:border-white/10 dark:bg-navy-900 dark:text-white"
                          placeholder="Account number"
                        />
                        <input
                          value={editForm.server}
                          onChange={(e) =>
                            setEditForm((p) => ({ ...p, server: e.target.value }))
                          }
                          className="h-9 min-w-[140px] shrink-0 rounded-lg border border-gray-200 bg-white px-2 text-xs leading-none text-navy-700 outline-none focus:border-brand-400 dark:border-white/10 dark:bg-navy-900 dark:text-white"
                          placeholder="Server"
                          autoComplete="off"
                          spellCheck={false}
                        />
                        <input
                          type="password"
                          value={editForm.tradingPassword}
                          onChange={(e) =>
                            setEditForm((p) => ({ ...p, tradingPassword: e.target.value }))
                          }
                          className="h-9 w-[100px] shrink-0 rounded-lg border border-gray-200 bg-white px-2 py-0 text-xs leading-none text-navy-700 outline-none focus:border-brand-400 dark:border-white/10 dark:bg-navy-900 dark:text-white"
                          placeholder="Trading password"
                        />
                        <SlaveAccountRowToolbar
                          row={row}
                          editingId={editingId}
                          actionLoadingId={actionLoadingId}
                          connectLoadingId={connectLoadingId}
                          deleteConfirmId={deleteConfirmId}
                          onEdit={() => startEditing(row)}
                          onSave={() => handleEditCredentials(row.id)}
                          onCancelEdit={cancelEditing}
                          onDeleteClick={() => {
                            setEditingId(null);
                            setDeleteConfirmId(row.id);
                          }}
                          onDeleteConfirm={() => handleDeleteAccount(row.id)}
                          onDeleteCancel={() => setDeleteConfirmId(null)}
                        />
                      </>
                    ) : (
                      <>
                        <span className="rounded-md bg-gray-200 px-2 py-0.5 text-xs font-semibold text-gray-700 dark:bg-white/10 dark:text-gray-300">
                          {row.accountType === "MT4" ? "MetaTrader4" : "MetaTrader5"}
                        </span>
                        <span className="rounded-md bg-brand-50 px-2 py-0.5 font-mono text-xs font-bold text-brand-700 dark:bg-brand-500/15 dark:text-brand-200">
                          {row.copierDisplayName || `S${row.slot}`}
                        </span>
                        <span className="rounded-md bg-gray-200 px-2 py-0.5 text-xs font-semibold text-gray-700 dark:bg-white/10 dark:text-gray-300">
                          S{row.slot}
                        </span>
                        <span className="shrink-0 font-medium text-navy-800 dark:text-white">
                          {row.accountNumber}
                        </span>
                        <span className="shrink-0 text-gray-600 dark:text-gray-400">{row.server}</span>
                        <span className="font-mono text-xs text-gray-500 dark:text-gray-500">
                          {row.tradingPasswordMasked}
                        </span>
                        <SlaveAccountRowToolbar
                          row={row}
                          editingId={editingId}
                          actionLoadingId={actionLoadingId}
                          connectLoadingId={connectLoadingId}
                          deleteConfirmId={deleteConfirmId}
                          canExtend={Boolean(row.expiringDate)}
                          extendTo={buildSlotExtensionPayUrl({
                            slot: row.slot,
                            codification:
                              row.copierDisplayName ||
                              `${effectiveClientId ?? session?.clientId ?? ""}S${row.slot}`,
                            subscriptionPath: "path2",
                            promoCode: PERSONAL_BROKER_PROMO_CODE,
                            currentExpiringDate: row.expiringDate,
                          })}
                          onEdit={() => startEditing(row)}
                          onSave={() => handleEditCredentials(row.id)}
                          onCancelEdit={cancelEditing}
                          onDeleteClick={() => {
                            setEditingId(null);
                            setDeleteConfirmId(row.id);
                          }}
                          onDeleteConfirm={() => handleDeleteAccount(row.id)}
                          onDeleteCancel={() => setDeleteConfirmId(null)}
                        />
                      </>
                    )}
                    </div>
                    <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-200 pt-2 dark:border-white/10">
                      <SlaveAccountRowStatus
                        row={row}
                        statusLoadingId={statusLoadingId}
                        onRefresh={refreshBrokerStatus}
                        variant="footer"
                      />
                      {!row.subscriptionExpired &&
                      !row.brokerConnected &&
                      !(row.linkedToCopier || row.tradeCopierAccountId) ? (
                        <button
                          type="button"
                          disabled={
                            connectLoadingId === row.id ||
                            editingId === row.id ||
                            statusLoadingId === row.id
                          }
                          onClick={() => handleConnectToSystem(row.id)}
                          className="rounded-lg bg-navy-800 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-navy-900 disabled:opacity-60 dark:bg-white/15 dark:hover:bg-white/25"
                        >
                          {connectLoadingId === row.id ? "Connecting…" : "Connect to system"}
                        </button>
                      ) : null}
                    </div>
                  </div>
                  {(row.linkedToCopier || row.tradeCopierAccountId) &&
                  !row.brokerConnected &&
                  editingId !== row.id &&
                  row.credentialsHint ? (
                    <p className="border-t border-gray-200 pt-2 text-center text-base font-bold leading-snug text-red-600 dark:border-white/10 dark:text-red-400">
                      {normalizeCredentialsHint(row.credentialsHint)}
                    </p>
                  ) : null}
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        <div className="mt-4 space-y-2">
          <p className="text-sm text-slate-600 dark:text-slate-400">
            Before deleting or editing the account, click Refresh.
          </p>
          <Link
            to="/client/subscription"
            className="inline-block text-sm font-semibold text-brand-500 hover:underline dark:text-brand-300"
          >
            Back to Subscription
          </Link>
        </div>
      </div>
    </div>
  );
}
